import React, { useState } from "react";
import Navbar from "./Navbar";
import Footer from "./Footer";
import { FaPhone, FaEnvelope, FaMapMarkerAlt } from "react-icons/fa";
// import loancalculatorbg from "../assets/loancalculatorbg.png";

const ContactUs: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    consent: true,
    message: ""
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({
    email: "",
    phone: ""
  });

  // Graceful fallbacks for environment variables to ensure 100% mockup accuracy if config is empty or whitespace
  const companyPhone = (import.meta.env.VITE_COMPANY_PHONE || "").trim();
  const companyEmail = (import.meta.env.VITE_COMPANY_EMAIL || "").trim();
  const companyAddress = (import.meta.env.VITE_COMPANY_ADDRESS || "").trim();
  const companyName = (import.meta.env.VITE_COMPANY_NAME || "").trim();
  const companyMapUrl = (import.meta.env.VITE_COMPANY_ADDRESS_MAP_URL || "").trim();

  const validateEmail = (email: string) => {
    if (!email) return "Email is required";
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return "Please enter a valid email address";
    }
    return "";
  };

  const validatePhone = (phone: string) => {
    if (!phone) return "Phone number is required";
    if (phone.length !== 10) {
      return "Phone number must be exactly 10 digits";
    }
    if (!/^[6-9]\d{9}$/.test(phone)) {
      return "Please enter a valid Indian mobile number starting with 6, 7, 8 or 9";
    }
    return "";
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name === "phone") {
      let clean = value.replace(/\D/g, "");
      if (clean.length > 0 && !/^[6-9]/.test(clean)) {
        clean = "";
      }
      clean = clean.slice(0, 10);
      setFormData({ ...formData, phone: clean });
      
      // Clear phone error dynamically if valid length reached
      if (clean.length === 10) {
        setErrors((prev) => ({ ...prev, phone: "" }));
      }
    } else {
      setFormData({ ...formData, [name]: value });
      if (name === "email") {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (emailRegex.test(value)) {
          setErrors((prev) => ({ ...prev, email: "" }));
        }
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailError = validateEmail(formData.email);
    const phoneError = validatePhone(formData.phone);

    if (emailError || phoneError) {
      setErrors({
        email: emailError,
        phone: phoneError
      });
      return;
    }

    setErrors({ email: "", phone: "" });
    setLoading(true);
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          access_key: import.meta.env.VITE_WEB3FORMS_ACCESS_KEY,
          subject: "New Contact Form Submission",
          sender_name: formData.fullName,
          sender_email: formData.email,
          consent: formData.consent ? "Accepted" : "Not Accepted",
          message: formData.message,
          phone: formData.phone,
          reply_to: formData.email
        })
      });
      const data = await response.json();
      if (data.success) {
        alert("Message sent successfully!");
        setFormData({ fullName: "", email: "", consent: true, phone: "", message: "" });
        setErrors({ email: "", phone: "" });
      } else {
        alert("Failed to send message. Please try again.");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      alert("An error occurred. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const getInputClass = (hasError: boolean) => {
    return `w-full px-4 py-3 bg-white/[0.03] border ${hasError ? 'border-red-500' : 'border-white/10'} rounded-xl text-white font-medium placeholder:text-slate-500 focus:outline-none focus:border-[#DE3226] focus:ring-1 focus:ring-[#DE3226] transition-all duration-200`;
  };

  return (
    <div className="min-h-screen w-full bg-[#D6EDFE] text-[var(--text-primary)] relative overflow-x-hidden font-sans selection:bg-[var(--primary)] selection:text-white">
      <div className="relative z-10 flex w-full flex-col items-center">
        <Navbar />

        {/* Contact Page Wrapper */}
        <section className="w-full bg-white pt-28 pb-16 flex flex-col items-center border-b border-slate-100">
          <div className="max-w-[1300px] w-full px-6 lg:px-20 py-12 flex flex-col gap-12 text-center">
            
            {/* Header */}
            <div className="flex flex-col items-center gap-3">
              <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-slate-200 bg-white shadow-sm text-[10px] font-black tracking-widest text-slate-800 uppercase leading-none">
                <span className="w-2 h-2 rounded-full bg-[#DE3226] animate-pulse" />
                <span>Support is just a message away</span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0F172A] leading-tight tracking-tight mt-4">
                Get in <span className="text-[#DE3226]">Touch</span>
              </h1>

              <p className="text-slate-500 font-medium text-sm sm:text-base lg:text-[17px] max-w-xl mx-auto leading-relaxed mt-2">
                Have questions about your salary advance? Our team is here to provide quick and personal support.
              </p>
            </div>

            {/* Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start w-full text-left mt-4">
              
              {/* Left Column: Contact details & Map (5 cols) */}
              <div className="col-span-1 lg:col-span-5 flex flex-col gap-6 w-full">
                
                {/* Info Card */}
                <div className="bg-white border border-slate-100/80 rounded-[32px] p-8 shadow-[0_15px_30px_rgba(0,0,0,0.02)] flex flex-col gap-6 relative overflow-hidden">
                  <div className="absolute top-0 left-0 right-0 h-[4px] bg-[#DE3226]" />
                  
                  <p className="text-[#0F172A] font-bold text-lg sm:text-xl leading-snug">
                    Your financial peace of mind is our top priority.
                  </p>

                  <div className="flex flex-col gap-5 mt-2">
                    {[
                      { icon: <FaPhone className="w-4 h-4" />, label: "Support Line", value: companyPhone, href: `tel:${companyPhone}` },
                      { icon: <FaEnvelope className="w-4 h-4" />, label: "Official Email", value: companyEmail, href: `mailto:${companyEmail}` },
                      { icon: <FaMapMarkerAlt className="w-4.5 h-4.5" />, label: "Headquarters", value: companyAddress, href: undefined }
                    ].map((item, i) => (
                      <div key={i} className="flex items-start gap-4">
                        <div className="w-10 h-10 rounded-xl bg-white border border-[#DE3226]/30 flex items-center justify-center text-[#DE3226] shrink-0 shadow-sm">
                          {item.icon}
                        </div>
                        <div className="flex flex-col pt-0.5">
                          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5 leading-none">
                            {item.label}
                          </span>
                          {item.href ? (
                            <a 
                              href={item.href} 
                              className="text-[#1E293B] font-extrabold hover:text-[#DE3226] transition-colors text-sm sm:text-[15px] leading-snug"
                            >
                              {item.value}
                            </a>
                          ) : (
                            <span className="text-[#1E293B] font-extrabold text-sm sm:text-[15px] leading-snug">
                              {item.value}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Google Map */}
                <div className="rounded-[32px] overflow-hidden border border-slate-100 shadow-[0_15px_30px_rgba(0,0,0,0.02)]">
                  <iframe
                    title={companyName}
                    src={companyMapUrl}
                    width="100%"
                    height="300"
                    className="border-0 block"
                    allowFullScreen
                    loading="lazy"
                  />
                </div>

              </div>

              {/* Right Column: Contact Form (7 cols) */}
              <div className="col-span-1 lg:col-span-7 w-full flex justify-center lg:justify-end">
                <form
                  onSubmit={handleSubmit}
                  className="rounded-[32px] p-8 md:p-10 flex flex-col gap-6 relative overflow-hidden shadow-[0_20px_50px_rgba(0,0,0,0.15)] w-full max-w-[620px] text-left bg-[#0B1220] border border-white/[0.05]"
                >
                  {/* Radial red glow at bottom-left */}
                  <div className="absolute left-[-15%] bottom-[-15%] w-60 h-60 bg-[#DE3226]/15 rounded-full blur-[70px] pointer-events-none z-0" />

                  <div className="relative z-10 flex flex-col gap-1.5 mb-2">
                    <h2 className="text-2xl sm:text-3xl font-black text-white tracking-tight">
                      Leave a Message
                    </h2>
                    <p className="text-slate-300 font-medium text-[14px]">
                      We'll get back to you as soon as possible.
                    </p>
                  </div>
                  <div className="relative z-10 flex flex-col gap-4">
                    <div className="flex flex-col gap-1.5 text-left w-full">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Full Name</label>
                      <input type="text" name="fullName" placeholder="Enter your full name" value={formData.fullName} onChange={handleChange} required className={getInputClass(false)} />
                    </div>

                    <div className="flex flex-col gap-1.5 text-left w-full">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Email Address</label>
                      <input type="email" name="email" placeholder="name@company.com" value={formData.email} onChange={handleChange} required className={getInputClass(!!errors.email)} />
                      {errors.email && (
                        <span className="text-red-500 text-xs font-semibold ml-1 mt-0.5">{errors.email}</span>
                      )}
                    </div>

                    <div className="flex flex-col gap-1.5 text-left w-full">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Phone Number</label>
                      <input type="tel" name="phone" placeholder="10-digit mobile number (e.g. 9876543210)" value={formData.phone} onChange={handleChange} required className={getInputClass(!!errors.phone)} />
                      {errors.phone && (
                        <span className="text-red-500 text-xs font-semibold ml-1 mt-0.5">{errors.phone}</span>
                      )}
                    </div>

                    <div className="flex flex-col gap-1.5 text-left w-full">
                      <label className="text-[11px] font-black text-slate-400 uppercase tracking-widest ml-1">Message</label>
                      <textarea name="message" placeholder="How can we help you?" value={formData.message} onChange={handleChange} rows={4} className={getInputClass(false)} />
                    </div>
                  </div>

                  <div className="relative z-10 flex items-start gap-3 px-1 mt-1">
                    <input
                      type="checkbox"
                      name="consent"
                      checked={formData.consent}
                      onChange={(e) => setFormData({ ...formData, consent: e.target.checked })}
                      className="mt-1 h-5 w-5 accent-[#DE3226] border-white/[0.1] bg-white/[0.03] rounded cursor-pointer shrink-0"
                    />
                    <label className="text-xs text-slate-300 font-medium leading-relaxed">
                      By submitting this form, you consent to receive communications regarding your inquiry via WhatsApp, email, or SMS.
                    </label>
                  </div>

                  <div className="relative z-10 mt-2">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full py-4 bg-[#DE3226] hover:bg-[#A82A1B] text-white font-bold text-center rounded-xl transition-all duration-200 active:scale-[0.99] shadow-md flex items-center justify-center gap-2 relative z-10 text-[15px] disabled:opacity-60 cursor-pointer"
                    >
                      <span>{loading ? "Sending..." : "Send Message"}</span>
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M14 5l7 7m0 0l-7 7m7-7H3" />
                      </svg>
                    </button>
                  </div>

                </form>
              </div>

            </div>

          </div>
        </section>

        <Footer />
      </div>
    </div>
  );
};

export default ContactUs;
